import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import LearningPanel from "@/components/LearningPanel";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { toast } from "sonner";
import { SlidersHorizontal, Loader2, X, Plus, Save, Facebook, Instagram, ShieldCheck, ShieldX, UserCheck } from "lucide-react";

function TagInput({ label, values, onChange, placeholder, tone, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  const toneCls = tone === "red" ? "bg-red-50 text-red-600" : tone === "green" ? "bg-green-50 text-green-600" : "bg-indigo-50 text-indigo-600";
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} data-testid={`${testid}-input`}
          className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-card text-sm focus:outline-none focus:ring-2 focus:ring-brass-500/40" />
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600"><Plus className="w-4 h-4" /></button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v) => (
            <span key={v} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${toneCls}`}>
              {v}
              <button type="button" onClick={() => onChange(values.filter((x) => x !== v))}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Rules() {
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const [accounts, setAccounts] = useState(null);
  const [activeId, setActiveId] = useState("");
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const canEdit = user?.role !== "client_viewer";

  const load = useCallback(() => {
    if (!ready) return;
    const q = scopeParam ? `?${scopeParam}` : "";
    api.get(`/accounts${q}`).then(({ data }) => {
      setAccounts(data);
      if (data.length) {
        const stillThere = data.find((a) => a.id === activeId);
        const chosen = stillThere || data[0];
        setActiveId(chosen.id);
        setRules({ ...chosen.rules });
      } else { setActiveId(""); setRules(null); }
    }).catch(() => setAccounts([]));
  }, [scopeParam, ready]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const selectAccount = (a) => { setActiveId(a.id); setRules({ ...a.rules }); };

  // Keep the always-remove tags in step with AI changes without discarding other unsaved edits.
  const onLearningChange = async (term, adds) => {
    if (term === undefined) {
      const { data } = await api.get(`/accounts${scopeParam ? `?${scopeParam}` : ""}`);
      const fresh = data.find((a) => a.id === activeId);
      if (fresh) setRules((r) => ({ ...r, always_remove: fresh.rules.always_remove || [] }));
      return;
    }
    setRules((r) => {
      const cur = r.always_remove || [];
      return { ...r, always_remove: adds ? (cur.includes(term) ? cur : [...cur, term]) : cur.filter((x) => x !== term) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/accounts/${activeId}/rules`, rules);
      toast.success("Rules saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Per-account moderation rules</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Rules</h1>
          </div>
          <ClientPicker />
        </div>

        {accounts === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : accounts.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <SlidersHorizontal className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Connect an account first to configure its rules.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[260px_1fr] gap-6">
            <div className="space-y-2">
              {accounts.map((a) => (
                <button key={a.id} onClick={() => selectAccount(a)} data-testid={`rules-account-${a.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition ${activeId === a.id ? "bg-indigo-50 border-indigo-200" : "bg-card border-slate-100 hover:border-slate-200"}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-slate-700 bg-slate-100 border border-slate-200 shrink-0`}>
                    {a.platform === "facebook" ? <Facebook className="w-4 h-4" /> : <Instagram className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p>
                    <p className="text-[11px] text-slate-400">{a.paused ? "Paused" : "Active"}</p>
                  </div>
                </button>
              ))}
            </div>

            {rules && (
              <div className="space-y-6 min-w-0">
              <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-6 space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-slate-700">Auto-hide confidence threshold</label>
                    <span className="text-sm font-bold text-indigo-600 font-mono" data-testid="threshold-value">{Math.round(rules.auto_hide_threshold * 100)}%</span>
                  </div>
                  <input type="range" min="50" max="99" value={Math.round(rules.auto_hide_threshold * 100)}
                    onChange={(e) => setRules({ ...rules, auto_hide_threshold: Number(e.target.value) / 100 })}
                    data-testid="threshold-slider" className="w-full accent-indigo-600" />
                  <p className="text-xs text-slate-400 mt-1">Comments the AI flags with confidence ≥ this are hidden automatically. Below it → Review Queue.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Default action on removal</label>
                  <div className="inline-flex p-1 bg-slate-100 rounded-lg gap-1">
                    {[["hide", "Hide (reversible)", ShieldX], ["delete", "Delete (permanent)", ShieldX]].map(([val, lbl]) => (
                      <button key={val} onClick={() => setRules({ ...rules, action: val })} data-testid={`action-${val}`}
                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${rules.action === val ? "bg-card text-indigo-600 shadow-sm" : "text-slate-500"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <TagInput label="Always-remove keywords" values={rules.always_remove || []} tone="red" testid="always-remove"
                    onChange={(v) => setRules({ ...rules, always_remove: v })} placeholder="e.g. bogus" />
                  <TagInput label="Never-remove keywords" values={rules.never_remove || []} tone="green" testid="never-remove"
                    onChange={(v) => setRules({ ...rules, never_remove: v })} placeholder="e.g. rally" />
                </div>

                <TagInput label="Trusted commenters (always ignored)" values={rules.trusted || []} tone="indigo" testid="trusted"
                  onChange={(v) => setRules({ ...rules, trusted: v })} placeholder="Commenter name" />

                <div className="flex justify-end pt-2 border-t border-slate-50">
                  <button onClick={save} disabled={saving} data-testid="save-rules"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save rules
                  </button>
                </div>
              </div>
              {activeId && <LearningPanel accountId={activeId} canEdit={canEdit} onRulesChange={onLearningChange} />}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
